import React, { useMemo, useState, useEffect } from "react";
import { AlertTriangle, Flag, Send } from "lucide-react";
import { REPORT_REASONS } from "../../services/reportsService";

const DEFAULT_OPTIONS = [
  { value: REPORT_REASONS.INAPPROPRIATE, label: "Inappropriate content" },
  { value: REPORT_REASONS.SPAM, label: "Spam" },
  { value: REPORT_REASONS.FRAUD, label: "Fraud or scam" },
  { value: REPORT_REASONS.HARASSMENT, label: "Harassment or abuse" },
  { value: REPORT_REASONS.MISLEADING, label: "Misleading information" },
  { value: REPORT_REASONS.OTHER, label: "Other" },
];

export default function ReportEntityDialog({
  open,
  entityLabel = "content",
  options = DEFAULT_OPTIONS,
  submitting = false,
  onSubmit,
  onClose,
}) {
  const [reason, setReason] = useState(options[0]?.value || REPORT_REASONS.INAPPROPRIATE);
  const [details, setDetails] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setReason(options[0]?.value || REPORT_REASONS.INAPPROPRIATE);
    setDetails("");
    setError("");
  }, [open, options]);

  const canSubmit = useMemo(() => details.trim().length >= 10 && !submitting, [details, submitting]);

  if (!open) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmed = details.trim();

    if (trimmed.length < 10) {
      setError("Please provide at least 10 characters of details.");
      return;
    }

    setError("");
    await onSubmit?.({ reason, details: trimmed });
  };

  return (
    <div className="fixed inset-0 z-[95] p-4 flex items-center justify-center" role="dialog" aria-modal="true">
      <button
        type="button"
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        aria-label="Close report dialog"
        onClick={onClose}
      />

      <form
        onSubmit={handleSubmit}
        className="relative w-full max-w-lg rounded-2xl border border-red-200 dark:border-red-900/50 bg-white dark:bg-neutral-900 shadow-2xl overflow-hidden"
      >
        <div className="px-5 py-4 border-b border-gray-200 dark:border-neutral-800 bg-gradient-to-r from-red-700 to-orange-600 text-white">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-lg bg-white/20 border border-white/25 flex items-center justify-center">
              <Flag className="w-5 h-5" />
            </span>
            <div>
              <p className="text-sm font-semibold">Report {entityLabel}</p>
              <p className="text-xs text-red-100">Help us keep the community safe.</p>
            </div>
          </div>
        </div>

        <div className="px-5 py-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5" htmlFor="report-reason">
              Reason
            </label>
            <select
              id="report-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full rounded-xl border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-800 text-sm text-gray-900 dark:text-white px-3 py-2.5 outline-none focus:ring-2 focus:ring-red-500"
            >
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5" htmlFor="report-details">
              Details
            </label>
            <textarea
              id="report-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Describe what happened and why this should be reviewed."
              rows={5}
              maxLength={1200}
              className="w-full rounded-xl border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-800 text-sm text-gray-900 dark:text-white px-3 py-2.5 outline-none focus:ring-2 focus:ring-red-500 resize-none"
            />
            <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400 text-right">
              {details.length}/1200
            </div>
          </div>

          {error && (
            <p className="text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-2.5 py-2 inline-flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" />
              {error}
            </p>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 h-10 rounded-xl border border-gray-200 dark:border-neutral-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-800 transition disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="flex-1 h-10 rounded-xl text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed transition inline-flex items-center justify-center gap-1.5"
            >
              {submitting ? <span className="orrith-loader orrith-loader-xs orrith-loader-on-dark" /> : <Send className="w-4 h-4" />}
              Submit report
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
